import {
    OAUTH_SCOPES,
    type OAuthScopeCategory,
    type OAuthScopeDefinition,
} from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';

/**
 * Access level the user grants to the client. `read` and `full` are the two preset radios; `custom`
 * is the state reached once the granted scopes no longer match either preset (the advanced view).
 */
export type OAuthAccessLevel = 'read' | 'full' | 'custom';

/**
 * Values managed by the consent form. `scopes` is the single source of truth for what the user
 * grants — the access level is derived from it. `trusted` gates approval for unverified clients.
 */
export interface OAuthConsentFormValues {
    scopes: string[];
    trusted: boolean;
}

/**
 * Group of requested scopes sharing a category, rendered as one collapsible section in the
 * advanced scopes view.
 */
export interface OAuthScopeGroup {
    category: OAuthScopeCategory;
    label: string;
    icon: IconDefinition;
    scopes: string[];
}

/**
 * Scope action segments that only read or query data. Every other action (`write`, `publish`,
 * `merge`, …) mutates, so it is excluded from the read-only access level.
 */
const READ_ONLY_ACTIONS = new Set(['read', 'ask', 'search']);

/**
 * Display label and icon for each scope category, kept in the consent layer (not api-client) since
 * it is purely presentational. Also defines the order categories appear in the consent UI.
 */
const SCOPE_CATEGORY_META: Record<OAuthScopeCategory, { label: string; icon: IconDefinition }> = {
    user: { label: 'User', icon: Icon.User },
    organization: { label: 'Organization', icon: Icon.Organization },
    space: { label: 'Space', icon: Icon.Space },
    'change-request': { label: 'Change requests', icon: Icon.ChangeRequest },
    site: { label: 'Site', icon: Icon.Globe },
    integration: { label: 'Integrations', icon: Icon.Integrations },
    openapi: { label: 'OpenAPI', icon: Icon.Code },
};

const SCOPE_CATEGORY_ORDER = Object.keys(SCOPE_CATEGORY_META) as OAuthScopeCategory[];

/**
 * Lookup from scope name to its registry definition (description, category, sensitivity).
 */
const SCOPE_DEFINITIONS = new Map<string, OAuthScopeDefinition>(
    (OAUTH_SCOPES as readonly OAuthScopeDefinition[]).map((scope) => [scope.name, scope])
);

/**
 * Whether a scope only reads data, inferred from its action segment (the part after the last
 * colon). Inferring from the name keeps this UI concern out of the api-client scope registry.
 */
export function isReadOnlyScope(scope: string): boolean {
    const action = scope.slice(scope.lastIndexOf(':') + 1);
    return READ_ONLY_ACTIONS.has(action);
}

/**
 * Human-readable description for a scope, falling back to the raw scope name if unknown.
 */
export function getScopeDescription(scope: string): string {
    return SCOPE_DEFINITIONS.get(scope)?.description ?? scope;
}

/**
 * Whether a scope carries a heightened blast radius (permissions, members, auth, publication, ...).
 */
export function isSensitiveScope(scope: string): boolean {
    return SCOPE_DEFINITIONS.get(scope)?.sensitive ?? false;
}

/**
 * The read-only subset of the requested scopes — what the "Read only" access level grants.
 */
export function getReadOnlyScopes(requestedScopes: string[]): string[] {
    return requestedScopes.filter(isReadOnlyScope);
}

/**
 * Group requested scopes by category in display order.
 */
export function groupScopesByCategory(scopes: string[]): OAuthScopeGroup[] {
    const byCategory = new Map<OAuthScopeCategory, string[]>();
    for (const scope of scopes) {
        const category = SCOPE_DEFINITIONS.get(scope)?.category;
        if (!category) {
            continue;
        }
        const group = byCategory.get(category) ?? [];
        group.push(scope);
        byCategory.set(category, group);
    }

    return SCOPE_CATEGORY_ORDER.filter((category) => byCategory.has(category)).map((category) => ({
        category,
        label: SCOPE_CATEGORY_META[category].label,
        icon: SCOPE_CATEGORY_META[category].icon,
        scopes: byCategory.get(category) ?? [],
    }));
}

/**
 * Derive which access level the current selection corresponds to, so the preset radios reflect the
 * granted scopes. Equal to all requested scopes → `full`; equal to the read-only subset → `read`;
 * anything else → `custom`.
 */
export function deriveAccessLevel(
    selectedScopes: string[],
    requestedScopes: string[]
): OAuthAccessLevel {
    const selected = new Set(selectedScopes);
    if (isSameScopeSet(selected, requestedScopes)) {
        return 'full';
    }
    if (isSameScopeSet(selected, getReadOnlyScopes(requestedScopes))) {
        return 'read';
    }
    return 'custom';
}

/**
 * The scopes granted by a given access level, given the scopes the client requested.
 */
export function getScopesForAccessLevel(
    level: Exclude<OAuthAccessLevel, 'custom'>,
    requestedScopes: string[]
): string[] {
    return level === 'read' ? getReadOnlyScopes(requestedScopes) : requestedScopes;
}

function isSameScopeSet(selected: Set<string>, scopes: string[]): boolean {
    return selected.size === scopes.length && scopes.every((scope) => selected.has(scope));
}
